/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import Select from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductsByTypeOptions, xAppState, xProductsByTypeActions } from "@monsma/App/Store/Reducers";
import { screen } from "@monsma/XDL/Helpers";
import { mergeWithCss } from "@monsma/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const mapStateToProps = (state: xAppState) => ({
    selectedProductsType: state.xProductsByType?.selectedProductsType,
});

const mapDispatchToProps = {
    setSelectedProductsType: (selectedProductsType: string) => (dispatch: Dispatch<xProductsByTypeActions>) => {
        dispatch({
            type: "xProductsByType/SetSelectedProductsType",
            selectedProductsType,
        });
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface ShowProductsByTypeSelectorStyles {
    wrapper?: InjectableCss;
    content?: InjectableCss;
    label?: TypographyPresentationProps;
    toggleWrapper?: InjectableCss;
    checkbox?: CheckboxProps;
    toggleLabel?: TypographyPresentationProps;
    firstToggleLabel?: TypographyPresentationProps;
    uncheckedToggleLabel?: TypographyPresentationProps;
    selectWrapper?: InjectableCss;
}

const toggleLabel: TypographyPresentationProps = {
    variant: "h4",
    color: "text.inverse",
    css: css`
        margin-bottom: 0;
        width: 200px;
    `,
};

const styles: ShowProductsByTypeSelectorStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("primary")};
            box-shadow: 0 6px 0 0 ${getColor("common.ui15")};
            padding-top: 95px;
            margin-bottom: 55px;
            position: relative;
            z-index: 1;
            &:after {
                content: " ";
                position: absolute;
                width: 0;
                height: 0;
                border-left: 90px solid transparent;
                border-right: 90px solid transparent;
                border-top: 50px solid ${getColor("primary")};
                right: 50%;
                transform: translateX(50%);
                filter: drop-shadow(0px 7px 0px ${getColor("common.ui15")});
                bottom: -46px;
            }
            ${screen("lte", "1185")} {
                padding: 40px 17px 0 17px;
            }
        `,
        // 1185px width, to match why monsma widget changes on responsive
    },
    content: {
        css: css`
            margin: 0 auto;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            padding-bottom: 35px;
        `,
    },
    label: {
        variant: "h2",
        color: "text.inverse",
        css: css`
            margin-bottom: 30px;
            text-align: center;
        `,
    },
    toggleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            ${screen("lte", "sm")} {
                display: none;
            }
        `,
    },
    checkbox: {
        css: css`
            width: auto;
            margin-left: 30px;
            margin-right: 30px;
        `,
    },
    toggleLabel,
    firstToggleLabel: {
        ...toggleLabel,
        css: css`
            ${toggleLabel?.css}
            text-align: right;
        `,
    },
    uncheckedToggleLabel: {
        css: css`
            opacity: 0.33;
        `,
    },
    selectWrapper: {
        css: css`
            display: none;
            max-width: 500px;
            width: 100%;
            ${screen("lte", "sm")} {
                display: block;
            }
        `,
    },
};

const ShowProductsByTypeSelector: React.FC<Props> = ({ selectedProductsType, setSelectedProductsType }) => {
    const toggleSwitch = () => {
        const value =
            selectedProductsType === ProductsByTypeOptions[0].value
                ? ProductsByTypeOptions[1].value
                : ProductsByTypeOptions[0].value;
        setSelectedProductsType(value);
    };

    const isChecked = selectedProductsType === ProductsByTypeOptions[0].value;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.content}>
                <Typography {...styles.label} as="div">{translate("Show Products by Type")}</Typography>
                <StyledWrapper {...styles.toggleWrapper}>
                    <Typography
                        {...mergeWithCss(styles.firstToggleLabel, isChecked ? {} : styles.uncheckedToggleLabel)}
                        as="div"
                    >
                        {translate("Building Materials")}
                    </Typography>
                    <Checkbox
                        variant="toggle"
                        {...styles.checkbox}
                        checked={isChecked}
                        onChange={toggleSwitch}
                    ></Checkbox>
                    <Typography {...mergeWithCss(styles.toggleLabel, isChecked ? styles.uncheckedToggleLabel : {})}
                                as="div">
                        {translate("Hearth Products")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.selectWrapper}>
                    <Select
                        onChange={(e: any) => {
                            const { value } = e.target;
                            setSelectedProductsType(value);
                        }}
                    >
                        {ProductsByTypeOptions.map(i => (
                            <option key={i.value} value={i.value}>
                                {translate(`Show ${i.displayName}`)}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShowProductsByTypeSelector),
    definition: {
        displayName: "Product By Type - Selector",
        group: "Basic",
        fieldDefinitions: [],
    },
};

export default widgetModule;
